import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
} from '../../components/common';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

/**
 * Reports index — FR-REP
 * Live dashboard KPIs + catalog of planned reports grouped by domain.
 */
export function ReportsIndexPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: reportsApi.dashboard,
  });

  const reportGroups: Array<{
    category: string;
    icon: React.ReactNode;
    tone: 'sage' | 'amber' | 'blue' | 'purple';
    items: Array<{ id: string; name: string; implemented?: boolean; to?: string }>;
  }> = [
    {
      category: 'Sales',
      tone: 'sage',
      icon: <svg {...ICON}><path d="M3 3v18h18"/><path d="M7 14l4-4 4 4 5-5"/></svg>,
      items: [
        { id: 'FR-REP-01.01', name: 'Daily sales with tender breakdown', implemented: true, to: '/reports/sales-inventory' },
        { id: 'FR-REP-01.02', name: 'Sales by cashier', implemented: true, to: '/reports/sales-inventory' },
        { id: 'FR-REP-01.03', name: 'Sales by category', implemented: true, to: '/reports/sales-inventory' },
        { id: 'FR-REP-01.04', name: 'Top customers by spend', implemented: true, to: '/reports/sales-inventory?tab=top-customers' },
        { id: 'FR-REP-01.05', name: 'Market basket analysis', implemented: true, to: '/reports/sales-inventory?tab=basket' },
      ],
    },
    {
      category: 'Inventory',
      tone: 'amber',
      icon: <svg {...ICON}><path d="M21 8l-9 4-9-4 9-4 9 4z"/><path d="M3 8v8l9 4 9-4V8"/></svg>,
      items: [
        { id: 'FR-REP-02.01', name: 'Inventory valuation (FIFO/WAC)', implemented: true, to: '/reports/sales-inventory' },
        { id: 'FR-REP-02.02', name: 'Inventory aging / slow-moving', implemented: true, to: '/reports/sales-inventory?tab=inv-aging' },
        { id: 'FR-REP-02.03', name: 'Inventory turnover ratio', implemented: true, to: '/reports/sales-inventory?tab=inv-turnover' },
        { id: 'FR-REP-02.04', name: 'ABC analysis', implemented: true, to: '/reports/sales-inventory?tab=inv-abc' },
      ],
    },
    {
      category: 'Finance',
      tone: 'blue',
      icon: <svg {...ICON}><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>,
      items: [
        { id: 'FR-REP-03.01', name: 'Profit & Loss', implemented: true, to: '/reports/finance-hr' },
        { id: 'FR-REP-03.02', name: 'Balance sheet', implemented: true, to: '/reports/finance-hr' },
        { id: 'FR-REP-03.03', name: 'Cash flow', implemented: true, to: '/reports/finance-hr?tab=cash-flow' },
        { id: 'FR-REP-03.04', name: 'AP aging', implemented: true, to: '/accounting/ap-aging' },
        { id: 'FR-REP-03.05', name: 'AR aging', implemented: true, to: '/accounting/ar-aging' },
      ],
    },
    {
      category: 'HR',
      tone: 'purple',
      icon: <svg {...ICON}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>,
      items: [
        { id: 'FR-REP-04.01', name: 'Headcount', implemented: true, to: '/reports/finance-hr' },
        { id: 'FR-REP-04.02', name: 'Turnover', implemented: true, to: '/reports/finance-hr?tab=turnover' },
        { id: 'FR-REP-04.03', name: 'Attendance summary', implemented: true, to: '/hrm/attendance' },
        { id: 'FR-REP-04.04', name: 'Payroll register', implemented: true, to: '/hrm/payroll' },
      ],
    },
  ];

  return (
    <div className="px-8 py-10 max-w-7xl mx-auto">
      <PageHeader
        eyebrow="Insights"
        title="Reports"
        subtitle="Live business metrics, plus the catalog of planned analytics."
      />

      {/* Live KPI strip */}
      {isLoading ? (
        <div className="card mb-8"><LoadingState rows={2} /></div>
      ) : error ? (
        <div className="mb-8"><ErrorState error={error} /></div>
      ) : data ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <KpiTile label="Sales today" value={`${fmtCurrency(data.today.salesTotal)}`} accent="sage" />
          <KpiTile label="Transactions today" value={data.today.salesCount} />
          <KpiTile label="Low stock" value={data.stockLowCount}
            accent={data.stockLowCount > 0 ? 'amber' : 'default'} />
          <KpiTile label="AR outstanding" value={`${fmtCurrency(data.arOutstanding)}`} />
        </div>
      ) : null}

      {/* Report catalog */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {reportGroups.map((group) => (
          <div key={group.category} className="card overflow-hidden">
            <div className="px-5 py-4 border-b border-ink-100 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-md flex items-center justify-center ${TONE_BG[group.tone]}`}>
                {group.icon}
              </div>
              <div>
                <div className="eyebrow text-ink-500">{group.category}</div>
                <div className="font-semibold text-ink-900">
                  {group.items.length} report{group.items.length === 1 ? '' : 's'}
                </div>
              </div>
            </div>
            <div className="divide-y divide-ink-100">
              {group.items.map((item) => (
                item.implemented && item.to ? (
                  <Link
                    key={item.id}
                    to={item.to}
                    className="flex items-center justify-between px-5 py-3 hover:bg-canvas-subtle/50 transition-colors group"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-ink-900 group-hover:text-sage-700 transition-colors truncate">
                        {item.name}
                      </div>
                      <div className="font-mono text-2xs text-ink-400 mt-0.5">{item.id}</div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                      <span className="badge bg-sage-50 text-sage-700 ring-1 ring-inset ring-sage-200 text-2xs">
                        Available
                      </span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-ink-400">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </div>
                  </Link>
                ) : (
                  <div
                    key={item.id}
                    className="flex items-center justify-between px-5 py-3 opacity-60"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-ink-700 truncate">{item.name}</div>
                      <div className="font-mono text-2xs text-ink-400 mt-0.5">{item.id}</div>
                    </div>
                    <span className="badge bg-canvas-subtle text-ink-500 ring-1 ring-inset ring-ink-200 text-2xs">
                      Planned
                    </span>
                  </div>
                )
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const ICON = {
  width: 16, height: 16, viewBox: '0 0 24 24',
  fill: 'none', stroke: 'currentColor', strokeWidth: 2,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
};

const TONE_BG: Record<string, string> = {
  sage:   'bg-sage-100 text-sage-700',
  amber:  'bg-amber-100 text-amber-800',
  blue:   'bg-blue-100 text-blue-700',
  purple: 'bg-purple-100 text-purple-700',
};

function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}
